/**
 * The binding refusal for an internal agent, in the portal's words (plan-029 WAPP-ACCESS; design §40.5, §40.19.4,
 * §40.13.2 `access.binding_refused`; D-12; AC-25).
 *
 * While the api half of the access list is on, a NEW binding — a number on the customer's SIP trunk, the website chat
 * widget — of an agent made for the customer's own team is refused `422 access_list_required` until its list is
 * turned on. The screens that create a binding draw {@link PartyAccessRequiredNote} for that refusal: the design's
 * sentence and a link to the agent's Channels tab, where "Who may call this agent" is set — instead of the api's
 * sentence. Every other refusal is left to the screen's own line. A screen that does not draw it still shows the api's
 * sentence, registered in both languages in the errors catalog.
 */
import { Link } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import type { ReactNode } from "react";

import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";

/** Whether a refused call is the binding refusal for an internal agent without its list. */
export function isPartyAccessRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === "access_list_required";
}

export interface PartyAccessRequiredNoteProps {
  /** The refused call's error; the note draws only for `access_list_required`. */
  readonly error: unknown;
  /** The agent the binding was for. */
  readonly voiceAgentId: string;
}

/** The design's `access.binding_refused`, with the way to the agent's list; nothing for any other refusal. */
export function PartyAccessRequiredNote({ error, voiceAgentId }: PartyAccessRequiredNoteProps): ReactNode {
  usePortalTranslation("callerProtection");
  if (!isPartyAccessRequired(error)) return null;
  return (
    <p className="c-note c-note-critical" role="alert" data-refusal="access_list_required">
      {portalText("callerProtection", "access.binding_refused")}{" "}
      <Link
        to="/agents/$voiceAgentId"
        params={{ voiceAgentId }}
        search={{
          tab: "channels",
          range: "7d",
          outcome: "all",
          q: "",
          conversation: "",
          analyticsRange: "14d",
          agentVersion: "",
          concern: "gaps",
        }}
      >
        {portalText("callerProtection", "access.open_channels")}
      </Link>
    </p>
  );
}
